<template>
  <div class="timelineContainer">
    <template v-if="isMetadataLoaded">
      <timeline-scrollable
        class="timeline"
        :class="{
          timeline_height_small: !hasThumbnails,
        }"
        :timerange="streamTimerange"
        :visible="visibleTimerange"
        :thumbnail-aspect-ratio="thumbnailAspectRatio"
        :scale-mode="scaleMode"
        :has-thumbnails="hasThumbnails"
        :show-thumbnails="showThumbnails"
        :value-checkbox-range="valueCheckboxRange"
        :timeline-data="timelineData"
        @scroll="handleScrolling"
        @movePlayer="$emit('movePlayer', $event)"
        @sliderMoved="handleSliderMoved"
        @zoom="$emit('zoom', $event)"
      />
      <!-- Shortcuts handler -->
      <timeline-shortcuts
        :full-length="visibleTimelineLength"
        @moveLeftCrop="moveLeftCrop"
        @moveRightCrop="moveRightCrop"
        @startLeftCropMoving="isCropMoving.left = true"
        @stopLeftCropMoving="isCropMoving.left = false"
        @startRightCropMoving="isCropMoving.right = true"
        @stopRightCropMoving="isCropMoving.right = false"
      />
    </template>
    <template v-else-if="isTimelineFailed">
      Timeline loading failed, see debug console for details
    </template>
    <template v-else>Loading...</template>
    <div class="btnPolicy">
      <a href="https://www.mangomolo.com/privacy" target="_blank" class="btnPolicyItem"
        >Privacy Policy
      </a>
      <div>|</div>
      <a href="https://www.mangomolo.com/contact" target="_blank" class="btnPolicyItem"
        >Contact Us
      </a>
    </div>
  </div>
</template>

<script>
import TimelineScrollable from "@/components/timeline/TimelineScrollable.vue";
import TimelineShortcuts from "@/components/timeline/TimelineShortcuts.vue";

import { mapState, mapGetters, mapActions } from "vuex";
import { areTimesEqual, roundToMilliseconds } from "@/utils/time.js";

// @group Timeline
// Timeline panel
export default {
  name: "TimelineEditor",
  components: {
    TimelineScrollable,
    TimelineShortcuts,
  },
  MIN_CROP_GAP: 0.001,
  props: {
    valueCheckboxRange: {
      type: Boolean,
      default: false,
    },
    showThumbnails: {
      type: Boolean,
      default: true,
    },
    /** Break intervals array */
    breaks: {
      type: Array,
      default: () => [],
    },
    /** display thumbnails on timeline or not */
    hasThumbnails: {
      type: Boolean,
      default: true,
    },
    /** how near timeline zoomed */
    scaleMode: {
      type: String,
      required: true,
    },
    /** player cursor time */
    playerTime: {
      type: Number,
      default: null,
    },
    /** [left_time, right_time] trim slider handles */
    trimSlider: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      /** is all required data loaded */
      // isMetadataLoaded: false,
      /** is crop moving by shortcuts */
      isCropMoving: {
        left: false,
        right: false,
      },
    };
  },
  computed: {
    ...mapState({
      cropLeftBound: (state) => state.timeline.sliderLeftBound,
      cropRightBound: (state) => state.timeline.sliderRightBound,
      timelineScaleStart: (state) => state.timeline.visibleTimelineLeft,
      timelineScaleEnd: (state) => state.timeline.visibleTimelineRight,
      streamStart: (state) => state.timeline.ndvrStreamStart,
      streamEnd: (state) => state.timeline.ndvrStreamEnd,
      timezone: (state) => state.timezone.timezone,
      thumbnailWidth: (state) => state.thumbnails.width,
      thumbnailHeight: (state) => state.thumbnails.height,
    }),
    ...mapGetters("timeline", [
      "visibleTimelineLength",
      "isTimelineInited",
      "isTimelineFailed",
    ]),
    // @vuese
    // Stream [start, end] array to pass into ScrollableTimeline
    streamTimerange() {
      return [this.streamStart, this.streamEnd];
    },
    // @vuese
    // Visible [star, end] array to pass into ScrollableTimeline
    visibleTimerange() {
      return [this.timelineScaleStart, this.timelineScaleEnd];
    },
    // @vuese
    // Thumbnail sides aspect ratio (width / height)
    thumbnailAspectRatio() {
      return this.hasThumbnails
        ? this.thumbnailWidth / this.thumbnailHeight
        : 270 / 155;
    },
    // @vuese
    // Additional timeline data to pass into ScrollableTimeline
    timelineData() {
      return {
        playerTime: this.playerTime,
        slider: this.trimSlider,
        isSliderSmooth: !this.isAnyCropMoving,
        breaks: this.breaks,
      };
    },
    // @vuese
    // Is any crop moving
    isAnyCropMoving() {
      return this.isCropMoving.left || this.isCropMoving.right;
    },
    // @vuese
    // Is all data required to render timeline loaded
    isMetadataLoaded() {
      return (
        (this.thumbnailAspectRatio || !this.hasThumbnails) &&
        this.isTimelineInited
      );
    },
  },
  methods: {
    ...mapActions("timeline", [
      "moveSliderLeftBound",
      "moveSliderRightBound",
      "scrollVisibleTimelineBackward",
      "scrollVisibleTimelineForward",
    ]),
    // @vuese
    // Update trim slider position
    handleSliderMoved([newLeft, newRight]) {
      const roundedLeft = roundToMilliseconds(newLeft);
      const roundedRight = roundToMilliseconds(newRight);

      this.$emit("trimSliderMoved", [roundedLeft, roundedRight]);

      if (
        this.trimSlider &&
        !areTimesEqual(roundedLeft, this.trimSlider[0])
      ) {
        this.$emit("movePlayer", roundedLeft);
      } else if (
        this.trimSlider &&
        !areTimesEqual(roundedRight, this.trimSlider[1])
      ) {
        this.$emit("movePlayer", roundedRight);
      }
    },
    // @vuese
    // Update visible range
    handleScrolling(deltaTime) {
      if (deltaTime > 0) {
        this.scrollVisibleTimelineForward(deltaTime);
      } else {
        this.scrollVisibleTimelineBackward(-deltaTime);
      }
    },
    // @vuese
    // Move left crop border
    // @arg value - time step in seconds
    moveLeftCrop(value) {
      const maxTarget = this.cropRightBound - this.$options.MIN_CROP_GAP;
      const target = this.cropLeftBound + value;
      if (target >= maxTarget) {
        value = maxTarget - this.cropLeftBound;
      }
      this.moveSliderLeftBound(roundToMilliseconds(value));
    },
    // @vuese
    // Move right crop border
    // @arg value - time step in seconds
    moveRightCrop(value) {
      const minTarget = this.cropLeftBound + this.$options.MIN_CROP_GAP;
      const target = this.cropRightBound + value;
      if (target <= minTarget) {
        value = minTarget - this.cropRightBound;
      }
      this.moveSliderRightBound(roundToMilliseconds(value));
    },
  },
};
</script>

<style lang="scss">
.timeline {
  /** position: relative; */
  height: 150px;
  // background-color: #000000;
  /** padding: 0 10px; */
  box-sizing: border-box;

  &_height_small {
    height: 120px;
  }
}

.btnPolicy {
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
}

.btnPolicyItem {
  margin: 0 5px;
  color: #fff;
}

.thumbnail-range {
  position: relative;
  display: flex;
  margin: 0;
  padding: 0;
}

.timelineContainer {
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  // background-color: black;
  /* width: 100%;
  height: 100%; */
}
</style>
